// File: /opt/stacks/workout/app/src/components/workout/WorkoutControls.js

import React from "react";
import { Button } from "../ui/button";
import { Play, Pause, SkipForward, SkipBack, CheckCircle2, Check, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Unified workout controls. Accepts all callbacks via props so it works
 * for both guided sessions and standalone timers.
 */
const WorkoutControls = ({
  isRepBased = false,
  isRunning = false,
  isLast = false,
  canGoBack = false,
  hasSkip = true,
  hasRoundSkip = false,
  onPlayPause,
  onSkipNext,
  onSkipBack,
  // Free training: no Previous step, which means nothing with no plan to
  // step through. Its log replaced the Lap button that sat here (#157).
  hideBack = false,
  // Free training puts its Rest button where Previous would be.
  backSlot = null,
  onRoundForward,
  onRoundBack,
  onFinish,
}) => {
  return (
    <div className="flex items-center justify-center gap-1 w-full h-20">
      {hasRoundSkip && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRoundBack}
          disabled={!canGoBack}
          aria-label="Previous round"
          className="h-10 w-10"
        >
          <ChevronsLeft className="h-6 w-6" />
        </Button>
      )}

      {hideBack ? (
        // Keeps play/pause centred between the gap and Finish.
        backSlot || <span className="w-10" aria-hidden="true" />
      ) : (
        hasSkip && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onSkipBack}
            disabled={!canGoBack}
            aria-label="Previous step"
          >
            <SkipBack className="h-7 w-7" />
          </Button>
        )
      )}

      {/* On a reps step the thing you do next is "done", not "pause", so
          Done is the big button and pause steps down to a secondary one. */}
      <Button
        size="lg"
        variant={isRepBased ? "outline" : "default"}
        onClick={onPlayPause}
        className={
          isRepBased
            ? "w-14 h-14 p-0 rounded-full mx-1 border-white/25 bg-transparent [&_svg]:size-6"
            : "w-20 h-20 rounded-full mx-2 [&_svg]:size-10"
        }
        aria-label={isRunning ? "Pause" : "Play"}
      >
        {isRunning ? (
          <Pause className={isRepBased ? "h-6 w-6" : "h-10 w-10"} />
        ) : (
          <Play className={isRepBased ? "h-6 w-6 ml-0.5" : "h-10 w-10 ml-0.5"} />
        )}
      </Button>

      {isRepBased ? (
        <Button
          size="icon"
          onClick={isLast ? onFinish : onSkipNext}
          className="w-20 h-20 rounded-full mx-1 bg-green-600 hover:bg-green-700 [&_svg]:size-10"
          aria-label={isLast ? "Finish workout" : "Complete reps"}
        >
          <Check className="h-10 w-10 text-white" />
        </Button>
      ) : hasSkip && (
        <Button
          variant="ghost"
          size="icon"
          onClick={isLast ? onFinish : onSkipNext}
          aria-label={isLast ? "Finish workout" : "Next step"}
        >
          {isLast ? (
            <CheckCircle2 className="h-7 w-7 text-green-500" />
          ) : (
            <SkipForward className="h-7 w-7" />
          )}
        </Button>
      )}

      {hasRoundSkip && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRoundForward}
          aria-label="Next round"
          className="h-10 w-10"
        >
          <ChevronsRight className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};

export default WorkoutControls;
